import React, { useEffect, useRef, useState } from "react";
import { Send, Terminal } from "lucide-react";
import { API } from "../lib/api";

export default function AIChat() {
  const [messages, setMessages] = useState([
    { role: "assistant", text: "Hi! I'm 2BF, the music AI of 2B FUNK. Ask me about funk, production, synthesizers, or listening recommendations." },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const sessionRef = useRef(`sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = async () => {
    const txt = input.trim();
    if (!txt || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: txt }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const resp = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: txt }),
      });
      if (!resp.ok || !resp.body) throw new Error("stream failed");
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const p of parts) {
          if (!p.startsWith("data: ")) continue;
          const payload = p.slice(6);
          if (payload === "[DONE]") continue;
          if (payload.startsWith("[ERROR]")) {
            setMessages((m) => {
              const c = [...m]; c[c.length - 1] = { role: "assistant", text: "⚠️ " + payload };
              return c;
            });
            continue;
          }
          setMessages((m) => {
            const c = [...m];
            c[c.length - 1] = { role: "assistant", text: c[c.length - 1].text + payload };
            return c;
          });
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m]; c[c.length - 1] = { role: "assistant", text: "⚠️ Errore di connessione." };
        return c;
      });
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="container-x py-16">
      <div className="max-w-4xl mx-auto">
        <div className="eyebrow mb-4">// AI Sonic Companion</div>
        <h1 className="section-heading mb-8">2BF <span className="text-[#B3FF00]">// Music</span> AI</h1>

        <div className="terminal-bg p-4 sm:p-6 min-h-[70vh] flex flex-col" data-testid="ai-chat-container">
          <div className="flex items-center gap-2 mb-4 pb-3 border-b border-[#B3FF00]/20">
            <Terminal className="w-4 h-4 text-[#B3FF00]" />
            <span className="font-mono-plex text-xs text-[#B3FF00] uppercase tracking-widest">2bf@funk:~$</span>
            <span className="ml-auto text-[10px] font-mono-plex text-white/30">Session {sessionRef.current.slice(-6)}</span>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto space-y-4 font-mono-plex text-sm mb-4 pr-2">
            {messages.map((m, i) => (
              <div key={`msg-${i}-${m.role}`} className={m.role === "user" ? "text-white" : "text-[#B3FF00]"} data-testid={`ai-msg-${i}`}>
                <span className="opacity-50 mr-2">{m.role === "user" ? "you>" : "2bf>"}</span>
                <span className="whitespace-pre-wrap">{m.text}{streaming && i === messages.length - 1 && <span className="animate-pulse">▊</span>}</span>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 border-t border-[#B3FF00]/20 pt-3">
            <span className="text-[#FF5E00] font-mono-plex text-sm">›</span>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask your music question..."
              disabled={streaming}
              className="flex-1 bg-transparent outline-none font-mono-plex text-sm text-white placeholder:text-white/30"
              data-testid="ai-chat-input"
            />
            <button onClick={send} disabled={streaming} className="text-[#B3FF00] hover:text-white disabled:opacity-40" data-testid="ai-chat-send-btn">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
      <div className="h-32" />
    </div>
  );
}
